import { Box, Chip, ChipDelete, Input } from "@mui/joy";
import { useSelector, useDispatch } from 'react-redux';
import { FilterList } from "@mui/icons-material";
import { useState, useRef, useEffect } from "react";
import { parseInput } from '../Internal/filterLogic';
import { useColorScheme } from "@mui/joy/styles";

// ── Suggestion item ──────────────────────────────────────────────────────────

function SuggestionItem({ suggestion, onSelect, active }) {
    const { mode } = useColorScheme();
    const isDark = mode === 'dark';

    const typeColor = isDark
        ? { particle: 'rgba(218,165,32,0.15)', keyword: 'rgba(244,67,54,0.12)', operator: 'rgba(96,125,139,0.15)' }
        : { particle: '#e3f2fd', keyword: '#fff8e1', operator: '#f3e5f5' };
    const activeBg = isDark ? 'rgba(255,255,255,0.10)' : '#e0e0e0';
    const hintColor = isDark ? '#9E9E9E' : '#888';

    return (
        <div
            onMouseDown={(e) => { e.preventDefault(); onSelect(suggestion); }}
            style={{
                padding: '4px 10px',
                cursor: 'pointer',
                borderRadius: '4px',
                background: active ? activeBg : typeColor[suggestion.type] || (isDark ? 'transparent' : '#fff'),
                fontFamily: 'monospace',
                fontSize: '0.85rem',
                userSelect: 'none',
            }}
        >
            {suggestion.label}
            <span style={{ marginLeft: 6, fontSize: '0.7rem', color: hintColor }}>
                {suggestion.type}
            </span>
        </div>
    );
}

// ── FilterBar ────────────────────────────────────────────────────────────────

function FilterBar() {
    const { mode } = useColorScheme();
    const hintColor = mode === 'dark' ? '#9E9E9E' : '#888';

    const dispatch = useDispatch();
    const filter = useSelector(state => state.topologyFilter);
    const clientFilters = useSelector(state => state.clientFilters);
    const finalStateNodes = useSelector(state => state.finalState.nodes);
    const finalStateData = useSelector(state => state.finalState.finalStateData);

    const [input, setInput] = useState('');
    const [pendingNodes, setPendingNodes] = useState({});
    const [focused, setFocused] = useState(false);
    const containerRef = useRef(null);
    const inputRef = useRef(null);

    const parsed = (finalStateNodes && finalStateData)
        ? parseInput(input, finalStateData, finalStateNodes)
        : { mode: 'idle', canCommit: false, suggestions: [] };

    // Close dropdown on outside click
    useEffect(() => {
        if (!focused) return;
        const handler = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target))
                setFocused(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [focused]);

    const handleSuggestionSelect = (suggestion) => {
        if (suggestion.type === 'particle') {
            const key = String(suggestion.node);
            setPendingNodes(prev => {
                const next = { ...prev };
                next[key] ? delete next[key] : (next[key] = true);
                return next;
            });
            setInput('');
        } else if (suggestion.type === 'keyword') {
            setInput(suggestion.label + ' ');
        } else if (suggestion.type === 'operator') {
            setInput(`${parsed.keyword} ${suggestion.label} `);
        }
        inputRef.current?.focus();
    };

    const commit = () => {
        if (parsed.canCommit) {
            dispatch({ type: 'ADD_CLIENT_FILTER', payload: { type: parsed.keyword, op: parsed.op, value: parsed.value } });
            setInput('');
            return;
        }
        const nodeKeys = Object.keys(pendingNodes).filter(k => pendingNodes[k]);
        if (nodeKeys.length >= 2) {
            nodeKeys.sort();
            const key = "[" + nodeKeys.join(",") + "]";
            dispatch({ type: 'ADD_FILTER', payload: { key, value: Object.fromEntries(nodeKeys.map(k => [k, true])) } });
            setPendingNodes({});
            setInput('');
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            commit();
        } else if (e.key === 'Backspace' && input === '') {
            const keys = Object.keys(pendingNodes);
            if (keys.length) {
                const next = { ...pendingNodes };
                delete next[keys[keys.length - 1]];
                setPendingNodes(next);
            }
        }
    };

    const pendingKeys = Object.keys(pendingNodes).filter(k => pendingNodes[k]);
    const showDropdown = focused && parsed.suggestions.length > 0;

    return (
        <Box ref={containerRef} sx={{ position: 'relative' }}>
            {/* Input bar */}
            <Box
                onClick={() => inputRef.current?.focus()}
                sx={{
                    display: 'flex', alignItems: 'center', flexWrap: 'wrap',
                    gap: 0.5, minHeight: '2.25rem', px: 1, py: 0.5,
                    border: '1px solid', borderColor: focused ? 'primary.400' : 'neutral.outlinedBorder',
                    borderRadius: 'sm', backgroundColor: 'background.surface', cursor: 'text',
                }}
            >
                <FilterList sx={{ color: 'neutral.400', fontSize: '1.1rem', flexShrink: 0 }} />

                {/* Active node filter chips */}
                {Object.keys(filter).map(key => {
                    const label = (finalStateNodes || [])
                        .filter(n => filter[key][n])
                        .map(n => finalStateData?.[n]?.name).join(", ");
                    return (
                        <Chip key={key} size="sm" variant="soft" color="primary"
                            endDecorator={<ChipDelete onDelete={() => dispatch({ type: 'REMOVE_FILTER', payload: key })} />}>
                            {label}
                        </Chip>
                    );
                })}

                {/* Active client filter chips */}
                {clientFilters.map((f, i) => (
                    <Chip key={i} size="sm" variant="soft" color="warning"
                        endDecorator={<ChipDelete onDelete={() => dispatch({ type: 'REMOVE_CLIENT_FILTER', payload: i })} />}>
                        {f.type} {f.op} {f.value}
                    </Chip>
                ))}

                {/* Pending node chips */}
                {pendingKeys.map(n => (
                    <Chip key={n} size="sm" variant="outlined" color="primary"
                        endDecorator={<ChipDelete onDelete={() => {
                            const next = { ...pendingNodes }; delete next[n]; setPendingNodes(next);
                        }} />}>
                        {finalStateData?.[n]?.name}
                    </Chip>
                ))}

                {/* Text input */}
                <input
                    ref={inputRef}
                    value={input}
                    onChange={e => { setInput(e.target.value); setFocused(true); }}
                    onFocus={() => setFocused(true)}
                    onKeyDown={handleKeyDown}
                    placeholder={!Object.keys(filter).length && !clientFilters.length && !pendingKeys.length ? "Filter…" : ""}
                    style={{
                        border: 'none', outline: 'none', background: 'transparent',
                        fontFamily: 'inherit', fontSize: '0.875rem', minWidth: '80px', flex: 1,
                        color: 'inherit',
                    }}
                />
            </Box>

            {/* Suggestions dropdown */}
            {showDropdown && (
                <Box sx={{
                    position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 100,
                    border: '1px solid', borderColor: 'neutral.outlinedBorder',
                    borderRadius: 'sm', backgroundColor: 'background.surface',
                    boxShadow: 'md', p: 0.5, display: 'flex', flexDirection: 'column', gap: 0.25,
                    maxHeight: '200px', overflowY: 'auto',
                }}>
                    {parsed.suggestions.map((s, i) => (
                        <SuggestionItem key={i} suggestion={s} onSelect={handleSuggestionSelect} />
                    ))}
                    {parsed.canCommit && (
                        <div style={{ padding: '4px 10px', fontSize: '0.75rem', color: hintColor, fontStyle: 'italic' }}>
                            Press Enter to add filter
                        </div>
                    )}
                    {pendingKeys.length >= 2 && parsed.mode !== 'complete' && (
                        <div style={{ padding: '4px 10px', fontSize: '0.75rem', color: hintColor, fontStyle: 'italic' }}>
                            Press Enter to add node group filter
                        </div>
                    )}
                </Box>
            )}
        </Box>
    );
}

export default FilterBar;
